import { styled } from '@mui/material';

const StyledLastUpdated = styled('p')({
  marginTop: 0,
  fontSize: 11,
  color: 'var(--color-neutral-500)',
});

const StyledSecurityPolicy = styled('div')`
  ol {
    padding-left: 14px;
  }
`;

export const SecurityPolicy = () => {
  return (
    <StyledSecurityPolicy>
      <StyledLastUpdated>Last updated: July 26, 2024</StyledLastUpdated>
      <ol>
        <li>
          <h3>Overview</h3>
          <p>
            Web application vulnerabilities account for the largest portion of
            attack vectors outside of malware. It is crucial that any web
            application be assessed for vulnerabilities and any vulnerabilities
            be remediated prior to production deployment.
          </p>
        </li>
        <li>
          <h3>Purpose</h3>
          <p>
            The purpose of this policy is to define web application security
            assessments within AppVelocity. Web application assessments are
            performed to identify potential or realized weaknesses as a result
            of inadvertent mis-configuration, weak authentication, insufficient
            error handling, sensitive information leakage, etc. Discovery and
            subsequent mitigation of these issues will limit the attack surface
            of AppVelocity services available both internally and externally as
            well as satisfy compliance with any relevant policies in place.
          </p>
        </li>
        <li>
          <h3>Scope</h3>
          <p>
            This policy covers all web application security assessments
            requested by any individual, group or department for the purposes of
            maintaining the security posture, compliance, risk management, and
            change control of technologies in use at AppVelocity.
          </p>

          <p>
            All web application security assessments will be performed by
            delegated security personnel either employed or contracted by App
            AppVelocity . All findings are considered confidential and are to be
            distributed to persons on a “need to know” basis. Distribution of
            any findings outside of AppVelocity is strictly prohibited unless
            approved by the Chief Information Officer.
          </p>

          <p>
            Any relationships within multi-tiered applications found during the
            scoping phase will be included in the assessment unless explicitly
            limited. Limitations and subsequent justification will be documented
            prior to the start of the assessment.
          </p>
        </li>
        <li>
          <h3>Policy</h3>
          <ol>
            <li>
              Web applications are subject to security assessments based on the
              following criteria:
              <ol>
                <li>
                  New or Major Application Release – will be subject to a full
                  assessment prior to approval of the change control
                  documentation and/or release into the live environment.
                </li>
                <li>
                  Third Party or Acquired Web Application – will be subject to
                  full assessment after which it will be bound to policy
                  requirements.
                </li>
                <li>
                  Point Releases – will be subject to an appropriate assessment
                  level based on the risk of the changes in the application
                  functionality and/or architecture.
                </li>
                <li>
                  Patch Releases – will be subject to an appropriate assessment
                  level based on the risk of the changes to the application
                  functionality and/or architecture.
                </li>
                <li>
                  Emergency Releases – An emergency release will be allowed to
                  forgo security assessments and carry the assumed risk until
                  such time that a proper assessment can be carried out.
                  Emergency releases will be designated as such by the Chief
                  Information Officer or an appropriate manager who has been
                  delegated this authority.
                </li>
                <li>
                  Annual Review – all applications will be subject to a full
                  annual review in its entirety to review potential risks of
                  functionality and/or architecture.
                </li>
              </ol>
            </li>
            <li>
              All security issues that are discovered during assessments must be
              mitigated based upon the following risk levels. The Risk Levels
              are based on the OWASP Risk Rating Methodology. Remediation
              validation testing will be required to validate fix and/or
              mitigation strategies for any discovered issues of Medium risk
              level or greater.
              <ol>
                <li>
                  High – Any high-risk issue must be fixed immediately or other
                  mitigation strategies must be put in place to limit exposure
                  before deployment. Applications with high risk issues are
                  subject to being taken off-line or denied release into the
                  live environment.
                </li>
                <li>
                  Medium – Medium risk issues should be reviewed to determine
                  what is required to mitigate and scheduled accordingly.
                  Applications with medium risk issues may be taken off-line or
                  denied release into the live environment based on the number
                  of issues and if multiple issues increase the risk to an
                  unacceptable level. Issues should be fixed in a patch/point
                  release unless other mitigation strategies will limit
                  exposure.
                </li>
                <li>
                  Low – Issue should be reviewed to determine what is required
                  to correct the issue and scheduled accordingly.
                </li>
              </ol>
            </li>
            <li>
              The following security assessment levels shall be established by
              the InfoSec organization or other designated organization that
              will be performing the assessments.
              <ol>
                <li>
                  Full – A full assessment is comprised of tests for all known
                  web application vulnerabilities using both automated and
                  manual tools based on the OWASP Testing Guide. A full
                  assessment will use manual penetration testing techniques to
                  validate discovered vulnerabilities to determine the overall
                  risk of any and all discovered.
                </li>
                <li>
                  Quick – A quick assessment will consist of a (typically)
                  automated scan of an application for the OWASP Top Ten web
                  application security risks at a minimum.
                </li>
                <li>
                  Targeted – A targeted assessment is performed to verify
                  vulnerability remediation changes or new application
                  functionality.
                </li>
              </ol>
            </li>
            <li>
              The current approved web application security assessment tools in
              use which will be used for testing are:
              <p>
                <a
                  href="https://github.com/OWASP/www-project-ide-vulscanner"
                  target="noopener noreferrer"
                >
                  OWASP IDE-VulScanner
                </a>
              </p>
              <p>
                <a
                  href="https://owasp.org/www-project-dependency-check/"
                  target="noopener noreferrer"
                >
                  {' '}
                  OWASP www-project-dependency-check
                </a>
              </p>
              <p>
                Other tools and/or techniques may be used depending upon what is
                found in the default assessment and the need to determine
                validity and risk are subject to the discretion of the Security
                Engineering team.
              </p>
            </li>
          </ol>

          <h3>Policy Compliance</h3>
          <p>
            <ol>
              <li>
                Compliance Measurement
                <p>
                  The Infosec team will verify compliance to this policy through
                  various methods, including but not limited to, business tool
                  reports, internal and external audits, and feedback to the
                  policy owner.
                </p>
              </li>
              <li>
                Exceptions
                <p>
                  Any exception to the policy must be approved by the Infosec
                  team in advance.
                </p>
              </li>
              <li>
                Non-Compliance
                <p>
                  An employee found to have violated this policy may be subject
                  to disciplinary action, up to and including termination of
                  employment.
                </p>
                <p>
                  Web application assessments are a requirement of the change
                  control process and are required to adhere to this policy
                  unless found to be exempt. All application releases must pass
                  through the change control process. Any web applications that
                  do not adhere to this policy may be taken offline until such
                  time that a formal assessment can be performed at the
                  discretion of the Chief Information Officer.
                </p>
              </li>
            </ol>
          </p>
        </li>
        <li>
          <h3>Related Standards, Policies and Processes</h3>
          <p>
            <a
              href="http://www.owasp.org/index.php/Category:OWASP_Top_Ten_Project"
              target="_blank"
              rel="noopener noreferrer"
            >
              OWASP Top Ten Project
            </a>
          </p>
          <p>
            <a
              href="https://owasp.org/www-project-web-security-testing-guide/"
              target="_blank"
              rel="noopener noreferrer"
            >
              OWASP Testing Guide
            </a>
          </p>
          <p>
            <a
              href="http://www.owasp.org/index.php/OWASP_Risk_Rating_Methodology"
              target="_blank"
              rel="noopener noreferrer"
            >
              OWASP Risk Rating Methodology
            </a>
          </p>
        </li>
        <li>
          <h3>Definitions and Terms</h3>
          <p>None</p>
        </li>
        <li>
          <h3>Revision History</h3>

          <p>
            <strong>Date of Change: </strong>July 2024
          </p>
          <p>
            <strong>Responsible: </strong>AppVelocity Team
          </p>
          <p>
            <strong>Summary of Change: </strong>Initial version
          </p>
        </li>
      </ol>
    </StyledSecurityPolicy>
  );
};
